import {
  LoadAllDepartments,
  LoadDepartmentsAccessedByEmployment,
} from 'src/app/department/state/department.actions';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import {
  StateToken,
  State,
  Action,
  StateContext,
  Selector,
  Actions,
  ofActionSuccessful,
} from '@ngxs/store';
import { of } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ToastService } from 'src/app/core/services/toast.service';
import { UserOptionsApiService } from 'src/app/core/services/user-options-api.service';
import { DepartmentState } from 'src/app/department/state/department.state';
import { IDepartmentWithGroups } from 'src/app/department/models/department.model';
import { IdName } from 'src/app/shared/models/id-name';
import {
  IUserAccess,
  ITenantUserAccess,
  IDepartmentInfo,
  IModulePrivileges,
  IUserRole,
  IUserTenantProfile,
} from 'src/app/shared/user-view/models/tenant-user-access.model';
import {
  buildDepartmentPauseById,
  DepartmentSelectOption,
  enrichSelectOptionsWithDepartmentPause,
} from 'src/app/shared/user-view/functions/enrich-select-options-with-department-pause';
import { patch } from '@ngxs/store/operators';
import { LoadDueListCounts, LoadMyDueListItems } from 'src/app/due-list/state/due-list.actions';
import {
  ActivateTableFilterScope,
  ClearFilterDetails,
  ResetCurrentFilter,
} from 'src/app/core/state/main-table-filter.actions';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { DueListRoutes } from 'src/app/core/enums/routes.enum';
import { DepartmentTypeEnum } from 'src/app/core/enums/department-type.enum';
import { ModuleNumbers } from 'src/app/core/enums/module-numbers.enum';
import { DepartmentStatus } from 'src/app/shared/enums/department-status.enum';
import { redirectAndBypassUnsavedDataCheck } from 'src/app/shared/functions/redirect-and-bypass-unsaved-data-check';
import { environment } from 'src/environments/environment';
import {
  ChangeCurrentSelectedDepartment,
  ChangeCurrentSelectedTenant,
  LoadTenantUserAccess,
  PatchCrewManagementType,
  PatchDepartmentModules,
  RefreshCurrentTenantProfile,
  UpdateDepartmentAvailability,
} from './tenant-user-access.actions';

export interface UserSettingsState {
  loaded: boolean;
  userAccess?: IUserAccess;
  availableTenants: ITenantUserAccess[];
  availableDepartments: IDepartmentInfo[];
  currentTenant?: ITenantUserAccess;
  currentDepartment?: IDepartmentInfo;
  currentDepartmentType?: DepartmentTypeEnum;
  currentDepartmentModules?: ModuleNumbers[];
  privileges: IModulePrivileges[];
  privilegesFlat: string[];
  userRoles: IUserRole[];
}

const USER_SETTINGS_STATE_TOKEN = new StateToken<UserSettingsState>('userSettings');
const LAST_SELECTED_TENANT_ID = 'lastSelectedTenantId';
const LAST_SELECTED_DEPARTMENT_ID = 'lastSelectedDepartmentId';
export const ALL_DEPS_TOKEN = 'all_deps';

@State<UserSettingsState>({
  name: USER_SETTINGS_STATE_TOKEN,
  defaults: {
    loaded: false,
    userAccess: undefined,
    availableTenants: [],
    availableDepartments: [],
    currentTenant: undefined,
    currentDepartment: undefined,
    currentDepartmentType: undefined,
    currentDepartmentModules: [],
    privileges: [],
    privilegesFlat: [],
    userRoles: [],
  },
})
@Injectable()
export class TenantUserAccessState {
  constructor(
    private service: UserOptionsApiService,
    private zone: NgZone,
    private router: Router,
    private toastService: ToastService,
    private actions: Actions
  ) {
    this.actions
      .pipe(
        ofActionSuccessful(ChangeCurrentSelectedDepartment, ChangeCurrentSelectedTenant),
        tap(() => {
          MainTableFilteringHelperService.clear();
        })
      )
      .subscribe();
  }

  @Action(LoadTenantUserAccess, { cancelUncompleted: true })
  loadTenantUserAccess(ctx: StateContext<UserSettingsState>, { tenantId }: LoadTenantUserAccess) {
    return this.service.getUserAccess(tenantId ?? '').pipe(
      switchMap((result: IUserAccess) => {
        const state = ctx.getState();

        if (result.tenantInfos.length === 0) {
          ctx.setState({
            ...state,
            loaded: true,
            userAccess: result,
          });
          return of(undefined);
        } else {
          const currentTenant = this.getInitialCurrentTenant(result.tenantInfos, tenantId);

          if (tenantId) {
            const previousTenantId = TenantUserAccessState.getLastSelectedTenantId();
            if (previousTenantId && previousTenantId !== currentTenant.id) {
              TenantUserAccessState.clearSelectedTenant();
            }
            TenantUserAccessState.setSelectedTenant(currentTenant.id);
          }

          const currentDepartment = this.resolveCurrentDepartment(
            ctx.getState(),
            currentTenant,
            !!tenantId
          );
          const rolePrivileges = currentDepartment
            ? currentTenant.rolePrivileges.find(
                (role) => role.departmentId === currentDepartment.id
              )
            : undefined;
          const privilegesFlat = this.combinePrivileges(
            currentTenant.privileges,
            rolePrivileges?.privileges ?? [],
            currentTenant.adminPrivileges ?? []
          );
          ctx.setState({
            ...state,
            loaded: true,
            userAccess: result,
            currentTenant,
            availableTenants: result.tenantInfos,
            availableDepartments: currentTenant.departmentInfos,
            currentDepartment,
            currentDepartmentType: currentDepartment?.departmentType,
            currentDepartmentModules: currentDepartment?.modules ?? [],
            privileges: currentTenant.privileges,
            privilegesFlat,
            userRoles: currentTenant.userRoles,
          });
          ctx.dispatch([new LoadDueListCounts(), new LoadDepartmentsAccessedByEmployment()]);
          return this.refreshCurrentTenantProfile(ctx, currentTenant.id);
        }
      })
    );
  }

  @Action(ChangeCurrentSelectedTenant, { cancelUncompleted: true })
  changeCurrentSelectedTenant(
    ctx: StateContext<UserSettingsState>,
    { selectedTenantId, withNavigation }: ChangeCurrentSelectedTenant
  ) {
    ctx.dispatch(new ResetCurrentFilter());
    TenantUserAccessState.clearSelectedTenant();
    TenantUserAccessState.setSelectedTenant(selectedTenantId);
    const { availableTenants } = ctx.getState();
    const newSelectedTenant = availableTenants.find(
      (tenant: ITenantUserAccess) => tenant.id === selectedTenantId
    );
    if (newSelectedTenant) {
      const currentDepartment = this.getCurrentDepartment(newSelectedTenant);
      const rolePrivileges = currentDepartment
        ? newSelectedTenant.rolePrivileges.find(
            (role) => role.departmentId === currentDepartment.id
          )
        : undefined;
      const privilegesFlat = this.combinePrivileges(
        newSelectedTenant.privileges,
        rolePrivileges?.privileges ?? [],
        newSelectedTenant.adminPrivileges ?? []
      );
      ctx.setState(
        patch({
          currentTenant: newSelectedTenant,
          availableDepartments: newSelectedTenant.departmentInfos,
          currentDepartment,
          currentDepartmentType: currentDepartment?.departmentType ?? DepartmentTypeEnum.LandBased,
          currentDepartmentModules: currentDepartment?.modules ?? [],
          privileges: newSelectedTenant.privileges,
          privilegesFlat,
        })
      );
      this.zone.run(() => {
        ctx.dispatch([
          new LoadAllDepartments(false),
          new LoadDueListCounts(),
          new ClearFilterDetails(),
          new LoadDepartmentsAccessedByEmployment(),
        ]);
        if (withNavigation) {
          this.toastService.success('tenantChangedSuccessfully');
          redirectAndBypassUnsavedDataCheck('/');
        }
      });
      return this.refreshCurrentTenantProfile(ctx, selectedTenantId);
    } else {
      this.toastService.success('somethingWentWrong');
      return of(undefined);
    }
  }

  @Action(ChangeCurrentSelectedDepartment, { cancelUncompleted: true })
  changeCurrentSelectedDepartment(
    ctx: StateContext<UserSettingsState>,
    { selectedDepartmentId, withNavigation, comingFromAllDeps }: ChangeCurrentSelectedDepartment
  ) {
    ctx.dispatch(new ResetCurrentFilter());
    if (selectedDepartmentId !== ALL_DEPS_TOKEN) {
      this.changeToSelectedDepartment(ctx, selectedDepartmentId, withNavigation, comingFromAllDeps);
    } else {
      TenantUserAccessState.clearSelectedDepartment();
      ctx.setState(
        patch({
          currentDepartment: { id: ALL_DEPS_TOKEN } as IDepartmentInfo,
        })
      );
      if (withNavigation) {
        ctx.dispatch([
          new LoadMyDueListItems(),
          new ClearFilterDetails(),
          new ActivateTableFilterScope(),
        ]);
        redirectAndBypassUnsavedDataCheck(DueListRoutes.Main);
      } else {
        ctx.dispatch(new ActivateTableFilterScope());
      }
    }
  }

  @Action(RefreshCurrentTenantProfile, { cancelUncompleted: true })
  refreshCurrentTenantProfileAction(
    ctx: StateContext<UserSettingsState>,
    { tenantId }: RefreshCurrentTenantProfile
  ) {
    const currentTenantId = tenantId || ctx.getState().currentTenant?.id;
    if (!currentTenantId) {
      return of(undefined);
    }

    return this.refreshCurrentTenantProfile(ctx, currentTenantId);
  }

  @Action(PatchCrewManagementType)
  patchCrewManagementType(
    ctx: StateContext<UserSettingsState>,
    { tenantId, departmentId, crewManagementType }: PatchCrewManagementType
  ) {
    const state = ctx.getState();
    const updatedDepartments = state.availableDepartments.map((d) =>
      d.id === departmentId ? { ...d, crewManagementType } : d
    );
    const updatedTenants = state.availableTenants.map((t) =>
      t.id === tenantId ? { ...t, departmentInfos: updatedDepartments } : t
    );
    const currentDepartment =
      departmentId === state.currentDepartment?.id
        ? updatedDepartments.find((d) => d.id === departmentId)
        : state.currentDepartment;
    ctx.setState(
      patch({
        availableDepartments: [...updatedDepartments],
        availableTenants: [...updatedTenants],
        currentDepartment,
      })
    );
  }

  @Action(PatchDepartmentModules)
  patchDepartmentModules(
    ctx: StateContext<UserSettingsState>,
    { tenantId, departmentId, modules }: PatchDepartmentModules
  ) {
    const state = ctx.getState();
    const updatedDepartments = state.availableDepartments.map((d) =>
      d.id === departmentId ? { ...d, modules } : d
    );
    const updatedTenants = state.availableTenants.map((t) =>
      t.id === tenantId ? { ...t, departmentInfos: updatedDepartments } : t
    );
    const currentDepartment =
      departmentId === state.currentDepartment?.id
        ? updatedDepartments.find((d) => d.id === departmentId)
        : state.currentDepartment;
    const isCurrentDepartment = departmentId === state.currentDepartment?.id;
    ctx.setState(
      patch({
        availableDepartments: [...updatedDepartments],
        availableTenants: [...updatedTenants],
        currentDepartment,
        ...(isCurrentDepartment ? { currentDepartmentModules: modules } : {}),
      })
    );
  }

  @Action(UpdateDepartmentAvailability)
  updateDepartmentAvailability(
    ctx: StateContext<UserSettingsState>,
    { departmentId, statusId }: UpdateDepartmentAvailability
  ) {
    const state = ctx.getState();
    const isPausedOrArchived =
      statusId === DepartmentStatus.Paused || statusId === DepartmentStatus.Archived;

    if (isPausedOrArchived) {
      // Remove department from availableDepartments
      const updatedDepartments = state.availableDepartments.filter((d) => d.id !== departmentId);
      const updatedTenants = state.availableTenants.map((t) => ({
        ...t,
        departmentInfos: t.departmentInfos.filter((d) => d.id !== departmentId),
      }));

      // If current department is being paused/archived, switch to first available or "all departments"
      let { currentDepartment } = state;
      let { currentDepartmentType } = state;
      let { currentDepartmentModules } = state;

      if (state.currentDepartment?.id === departmentId) {
        if (updatedDepartments.length > 0) {
          currentDepartment = updatedDepartments[0];
          currentDepartmentType = currentDepartment.departmentType;
          currentDepartmentModules = currentDepartment.modules;
        } else {
          // Fallback to "all departments" if no departments available
          currentDepartment = { id: ALL_DEPS_TOKEN } as IDepartmentInfo;
          currentDepartmentType = undefined;
          currentDepartmentModules = [];
        }
      }

      ctx.setState(
        patch({
          availableDepartments: [...updatedDepartments],
          availableTenants: [...updatedTenants],
          currentDepartment,
          currentDepartmentType,
          currentDepartmentModules,
        })
      );
    } else {
      // Status is Active or NotCompleted - add department back if it exists in original userAccess
      const { currentTenant } = state;
      if (currentTenant) {
        const originalDepartment = currentTenant.departmentInfos.find((d) => d.id === departmentId);
        if (originalDepartment) {
          // Check if department is already in availableDepartments
          const existsInAvailable = state.availableDepartments.some((d) => d.id === departmentId);
          if (!existsInAvailable) {
            // Add it back to availableDepartments
            const updatedDepartments = [...state.availableDepartments, originalDepartment];
            const updatedTenants = state.availableTenants.map((t) =>
              t.id === currentTenant.id ? { ...t, departmentInfos: updatedDepartments } : t
            );

            ctx.setState(
              patch({
                availableDepartments: [...updatedDepartments],
                availableTenants: [...updatedTenants],
              })
            );
          }
        }
      }
    }
  }

  @Selector()
  static tenantLoaded(state: UserSettingsState) {
    return state.loaded;
  }

  @Selector()
  static currentTenant(state: UserSettingsState): ITenantUserAccess {
    return state.currentTenant as ITenantUserAccess;
  }

  @Selector()
  static currentDepartment(state: UserSettingsState): IDepartmentInfo {
    return state.currentDepartment as IDepartmentInfo;
  }

  @Selector()
  static currentDepartmentType(state: UserSettingsState): DepartmentTypeEnum {
    return state.currentDepartmentType as DepartmentTypeEnum;
  }

  @Selector()
  static currentDepartmentModules(state: UserSettingsState): ModuleNumbers[] {
    return state.currentDepartmentModules as ModuleNumbers[];
  }

  @Selector()
  static isWorkAndRestEnabled(state: UserSettingsState): boolean {
    return (
      (state.currentDepartmentModules?.includes(ModuleNumbers.WorkAndRest) &&
        state.currentDepartment?.departmentType !== DepartmentTypeEnum.LandBased) ??
      false
    );
  }

  @Selector()
  static userAccess(state: UserSettingsState) {
    return state.userAccess as IUserAccess;
  }

  @Selector()
  static getPrivileges(state: UserSettingsState) {
    return state.privileges;
  }

  @Selector()
  static isProductOwner(state: UserSettingsState) {
    return state.userAccess?.isProductOwner;
  }

  @Selector()
  static getAllAvailableTenants(state: UserSettingsState): ITenantUserAccess[] {
    return state.availableTenants;
  }

  @Selector()
  static getAllAvailableDepartments(state: UserSettingsState): IDepartmentInfo[] {
    return state.availableDepartments;
  }

  @Selector([TenantUserAccessState.getAllAvailableDepartments])
  static departmentPauseById(availableDepartments: IDepartmentInfo[]): Record<string, boolean> {
    return buildDepartmentPauseById(availableDepartments);
  }

  @Selector([DepartmentState.getAllDepartments, TenantUserAccessState.departmentPauseById])
  static getAllDepartmentsWithPause(
    allDepartments: IdName[],
    pauseById: Record<string, boolean>
  ): DepartmentSelectOption[] {
    return enrichSelectOptionsWithDepartmentPause(allDepartments, pauseById);
  }

  @Selector([
    DepartmentState.getDepartmentsAccessedByEmployment,
    TenantUserAccessState.departmentPauseById,
  ])
  static getDepartmentsAccessedByEmploymentWithPause(
    departments: IdName[],
    pauseById: Record<string, boolean>
  ): DepartmentSelectOption[] {
    return enrichSelectOptionsWithDepartmentPause(departments, pauseById);
  }

  @Selector([DepartmentState.getDepartmentsForMenuId, TenantUserAccessState.departmentPauseById])
  static getDepartmentsForMenuIdWithPause(
    departments: IdName[],
    pauseById: Record<string, boolean>
  ): DepartmentSelectOption[] {
    return enrichSelectOptionsWithDepartmentPause(departments, pauseById);
  }

  @Selector([DepartmentState.getDepartmentsForUser, TenantUserAccessState.departmentPauseById])
  static getDepartmentsForUserWithPause(
    departments: IDepartmentWithGroups[] | undefined,
    pauseById: Record<string, boolean>
  ): DepartmentSelectOption[] {
    return enrichSelectOptionsWithDepartmentPause(
      (departments ?? []).map((department) => ({
        id: department.id,
        name: department.name,
      })),
      pauseById
    );
  }

  @Selector([
    DepartmentState.getAllDepartmentsForSelectedTenant,
    TenantUserAccessState.departmentPauseById,
  ])
  static getAllDepartmentsForSelectedTenantWithPause(
    departments: IdName[],
    pauseById: Record<string, boolean>
  ): DepartmentSelectOption[] {
    return enrichSelectOptionsWithDepartmentPause(departments, pauseById);
  }

  @Selector()
  static hasMultipleDepartments(state: UserSettingsState): boolean {
    return state.availableDepartments.length > 1;
  }

  @Selector()
  static currentTenantId(state: UserSettingsState): string {
    return state.currentTenant?.id as string;
  }

  @Selector()
  static currentDepartmentId(state: UserSettingsState): string {
    return state.currentDepartment?.id as string;
  }

  @Selector()
  static getMarineManagerEnabled(state: UserSettingsState): boolean {
    const envEnabled =
      environment.marineManagerEnabled === 'true' || environment.marineManagerEnabled === true;
    return envEnabled && state.currentDepartment?.marineManagerEnabled === true;
  }

  @Selector()
  static getPrivilegesFlat(state: UserSettingsState): string[] {
    return state.privilegesFlat;
  }

  private changeToSelectedDepartment(
    ctx: StateContext<UserSettingsState>,
    selectedDepartmentId: string,
    withNavigation: boolean,
    comingFromAllDeps?: boolean
  ) {
    const { availableDepartments } = ctx.getState();
    const newSelectedDepartment = availableDepartments.find(
      (dep: IDepartmentInfo) => dep.id === selectedDepartmentId
    );
    if (newSelectedDepartment) {
      const currentTenant = ctx.getState().currentTenant as ITenantUserAccess;
      const rolePrivileges = currentTenant.rolePrivileges.find(
        (role) => role.departmentId === newSelectedDepartment.id
      );
      const privilegesFlat = this.combinePrivileges(
        currentTenant.privileges,
        rolePrivileges?.privileges ?? [],
        currentTenant.adminPrivileges ?? []
      );
      TenantUserAccessState.setSelectedDepartment(selectedDepartmentId);
      ctx.setState(
        patch({
          currentDepartment: newSelectedDepartment,
          currentDepartmentType: newSelectedDepartment.departmentType,
          currentDepartmentModules: newSelectedDepartment.modules,
          privilegesFlat,
        })
      );
      ctx.dispatch([
        new LoadDueListCounts(),
        new ClearFilterDetails(),
        new ActivateTableFilterScope(),
      ]);
      if (withNavigation && !comingFromAllDeps) {
        this.zone.run(() => {
          this.toastService.success('departmentChangedSuccessfully');
          redirectAndBypassUnsavedDataCheck('/');
        });
      }
    } else {
      this.toastService.success('somethingWentWrong');
    }
  }

  private getPrivilegesFlatList(privilegesGrouped: IModulePrivileges[]): string[] {
    let flatPrivileges: string[] = [];
    privilegesGrouped.forEach((groupedPrivilege: IModulePrivileges) => {
      if (groupedPrivilege.privileges?.length > 0) {
        flatPrivileges = flatPrivileges.concat(groupedPrivilege.privileges);
      }
    });
    return flatPrivileges;
  }

  private combinePrivileges(
    privileges: IModulePrivileges[],
    rolePrivileges: IModulePrivileges[],
    adminPrivileges: IModulePrivileges[]
  ): string[] {
    return Array.from(
      new Set([
        ...this.getPrivilegesFlatList(privileges),
        ...this.getPrivilegesFlatList(rolePrivileges),
        ...this.getPrivilegesFlatList(adminPrivileges),
      ])
    );
  }

  private refreshCurrentTenantProfile(ctx: StateContext<UserSettingsState>, tenantId: string) {
    return this.service.getTenantUserProfile(tenantId).pipe(
      tap((profile) => this.patchUserAccessProfile(ctx, profile)),
      catchError(() => of(undefined))
    );
  }

  private patchUserAccessProfile(
    ctx: StateContext<UserSettingsState>,
    profile: IUserTenantProfile
  ): void {
    const state = ctx.getState();
    if (!state.userAccess) {
      return;
    }

    ctx.setState(
      patch({
        userAccess: {
          ...state.userAccess,
          id: profile.userId,
          userProfileId: profile.id,
          tenantProfileTenantId: profile.tenantId,
          firstName: profile.firstName,
          middleName: profile.middleName,
          lastName: profile.lastName,
          name: profile.fullName,
          profilePicture: profile.profilePicture ?? undefined,
          profilePictureMiniature: profile.profilePictureMiniature ?? undefined,
          signature: profile.signature ?? undefined,
        },
      })
    );
  }

  private getInitialCurrentTenant(
    tenantInfos: ITenantUserAccess[],
    requestedTenantId?: string
  ): ITenantUserAccess {
    const selectedTenantId = requestedTenantId || TenantUserAccessState.getLastSelectedTenantId();

    return tenantInfos.find(({ id }) => id === selectedTenantId) || tenantInfos[0];
  }

  private getCurrentDepartment(tenant: ITenantUserAccess): IDepartmentInfo | undefined {
    const lastSelectedDepartmentId = TenantUserAccessState.getLastSelectedDepartmentId();
    if (lastSelectedDepartmentId) {
      const foundDepartment = tenant.departmentInfos.find(
        ({ id }) => id === lastSelectedDepartmentId
      );
      if (foundDepartment) {
        return foundDepartment as IDepartmentInfo;
      }
      // If last selected department is not found, fall back to employment department or first department
    }
    return (
      (tenant.departmentInfos.find((dep) => dep.isEmploymentDepartment) as IDepartmentInfo) ||
      tenant.departmentInfos[0]
    );
  }

  private resolveCurrentDepartment(
    state: UserSettingsState,
    tenant: ITenantUserAccess,
    isTenantScopedReload: boolean
  ): IDepartmentInfo | undefined {
    if (
      isTenantScopedReload &&
      state.currentTenant?.id === tenant.id &&
      state.currentDepartment?.id &&
      state.currentDepartment.id !== ALL_DEPS_TOKEN
    ) {
      const preservedDepartment = tenant.departmentInfos.find(
        (dep) => dep.id === state.currentDepartment?.id
      );
      if (preservedDepartment) {
        TenantUserAccessState.setSelectedDepartment(preservedDepartment.id);
        return preservedDepartment as IDepartmentInfo;
      }
    }

    return this.getCurrentDepartment(tenant);
  }

  static getLastSelectedTenantId(): string {
    return localStorage.getItem(LAST_SELECTED_TENANT_ID) as string;
  }

  static getUserRoles(state: UserSettingsState): IUserRole[] {
    return state.userRoles || [];
  }

  static getUserRolesForCurrentlySelectedDepartment(state: any): string[] {
    return (state.userSettings.userRoles || [])
      .filter((role: IUserRole) =>
        role.issuedForDepartments.includes(state.userSettings.currentDepartment?.id || '')
      )
      .map((role: IUserRole) => role.role.id);
  }

  static getLastSelectedDepartmentId(): string {
    return localStorage.getItem(LAST_SELECTED_DEPARTMENT_ID) as string;
  }

  static setSelectedTenant(tenantId: string): void {
    localStorage.setItem(LAST_SELECTED_TENANT_ID, tenantId);
  }

  static setSelectedDepartment(departmentId: string): void {
    localStorage.setItem(LAST_SELECTED_DEPARTMENT_ID, departmentId);
  }

  static clearSelectedDepartment(): void {
    localStorage.removeItem(LAST_SELECTED_DEPARTMENT_ID);
  }

  static clearSelectedTenant(): void {
    localStorage.removeItem(LAST_SELECTED_TENANT_ID);
    localStorage.removeItem(LAST_SELECTED_DEPARTMENT_ID);
  }
}
